import { supabase } from '../supabaseClient';

export interface Site {
  id: string;
  company_id: string;
  client_contract_id?: string | null;
  client_contract?: { name?: string | null; status?: string | null } | Array<{ name?: string | null; status?: string | null }> | null;
  name: string;
  [key: string]: unknown;
}

export interface Checkpoint {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  site_id: string;
  geofence_radius: number;
  location: string;
  [key: string]: unknown;
}

export interface CreateCheckpointParams {
  name: string;
  code?: string | null;
  description?: string | null;
  siteId: string;
  latitude: number;
  longitude: number;
  geofenceRadius?: number;
}

export const checkpointService = {
  async getCurrentUserCompanyId(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return (data as { company_id: string }).company_id;
  },

  async fetchSites(): Promise<Site[]> {
    const companyId = await this.getCurrentUserCompanyId();
    const { data, error } = await supabase
      .from('sites')
      .select(`
        *,
        client_contract:client_contracts!sites_client_contract_id_fkey(name, status)
      `)
      .eq('company_id', companyId);

    if (error) throw error;
    return ((data ?? []) as Site[]).filter((site) => {
      const clientContract = Array.isArray(site.client_contract) ? site.client_contract[0] : site.client_contract;
      return clientContract?.status !== 'archived';
    });
  },

  async createCheckpoint({ name, code, description, siteId, latitude, longitude, geofenceRadius }: CreateCheckpointParams): Promise<Checkpoint> {
    const { data, error } = await supabase
      .from('checkpoints')
      .insert({
        name,
        code: code ?? null,
        description: description ?? null,
        site_id: siteId,
        geofence_radius: geofenceRadius ?? 50,
        location: `SRID=4326;POINT(${longitude} ${latitude})`,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Checkpoint;
  },

  async fetchCheckpoints(): Promise<Checkpoint[]> {
    const companyId = await this.getCurrentUserCompanyId();
    const { data, error } = await supabase
      .from('checkpoints')
      .select('*, sites(name)')
      .eq('sites.company_id', companyId);

    if (error) throw error;
    return (data ?? []) as Checkpoint[];
  },

  async fetchCheckpointsBySite(siteId: string): Promise<Checkpoint[]> {
    const { data, error } = await supabase
      .from('checkpoints')
      .select('*')
      .eq('site_id', siteId);

    if (error) throw error;
    return (data ?? []) as Checkpoint[];
  },

  async deleteCheckpoint(checkpointId: string): Promise<void> {
    const { error } = await supabase
      .from('checkpoints')
      .delete()
      .eq('id', checkpointId);

    if (error) throw error;
  },
};
